import { BUSINESS_UNIT_TYPES, VALUE_CHAIN_CONNECTION_TYPES } from 'constants/interfaces';

import type IAIDraftUnitItem from 'store/slices/valueChainMap/interfaces/IAIDraftUnitItem';
import type {
  IAIDraftColumn,
  IAIDraftConnection,
  IAIDraftConnectionUnit,
  IAIDraftUnit,
} from 'store/slices/valueChainMap/interfaces/IAIDraftUnitItem';
import type { IGetValueChainBoardResponse } from 'store/slices/valueChainMap/interfaces/IGetValueChainBoardResponse';
import type {
  IValueChainGoodsConnection,
} from 'store/slices/valueChainMap/interfaces/IValuChainGoodsConnection';
import type { IValueChainBoard } from 'store/slices/valueChainMap/interfaces/IValueChainBoard';
import type { IValueChainBoardColumn } from 'store/slices/valueChainMap/interfaces/IValueChainBoardColumn';
import type { IValueChainLogicalConnection } from 'store/slices/valueChainMap/interfaces/IValueChainLogicalConnection';
import type { IValueChainManufactureConnection } from 'store/slices/valueChainMap/interfaces/IValueChainManufactureConnection';
import type { IValueChainRecord } from 'store/slices/valueChainMap/interfaces/IValueChainRecord';
import type { IValueChainServiceConnection } from 'store/slices/valueChainMap/interfaces/IValueChainServiceConnection';
import type { IValueChainUnit } from 'store/slices/valueChainMap/interfaces/IValueChainUnit';

type TDraftValueChainBoardColumn = IValueChainBoardColumn & {
  draft?: boolean,
};

type TDraftConnection = (
  IValueChainLogicalConnection
  | IValueChainServiceConnection
  | IValueChainGoodsConnection
  | IValueChainManufactureConnection
) & {
  draft?: boolean,
};

type TDraftValueChainUnit = IValueChainUnit & {
  draft?: boolean,
  /**
   * Stable server-side draft unit identifier.
   * Used for actions like deleting draft units instead of internal negative ids.
   */
  draftServerId?: number,
  lConToUnits?: TDraftConnection[],
  sConToUnits?: TDraftConnection[],
  gsConToUnits?: TDraftConnection[],
  mConToUnits?: TDraftConnection[],
};

const DRAFT_COLUMN_ID_OFFSET = 100000;
const DRAFT_UNIT_ID_OFFSET = 200000;
const DRAFT_RECORD_ID_OFFSET = 300000;
const DRAFT_CONNECTION_ID_OFFSET = 400000;

const getDraftColumnId = (index: number): number => -(DRAFT_COLUMN_ID_OFFSET + index + 1);
const getDraftUnitId = (serverId: number, fallbackIndex: number): number => (
  serverId > 0 ? -(DRAFT_UNIT_ID_OFFSET + serverId) : -(DRAFT_UNIT_ID_OFFSET + fallbackIndex + 1)
);
const getDraftRecordId = (serverId: number, fallbackIndex: number): number => (
  serverId > 0 ? -(DRAFT_RECORD_ID_OFFSET + serverId) : -(DRAFT_RECORD_ID_OFFSET + fallbackIndex + 1)
);
const getDraftConnectionId = (index: number): number => -(DRAFT_CONNECTION_ID_OFFSET + index + 1);

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const toNumber = (value: unknown, fallback: number = 0): number => {
  if (isFiniteNumber(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  }

  return fallback;
};

const toString = (value: unknown, fallback: string = ''): string => (
  typeof value === 'string' ? value : fallback
);

const toStringArray = (value: unknown): string[] => (
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
);

const insertAtIndex = <T>(items: T[], item: T, index: number): T[] => {
  const safeIndex = Math.max(0, Math.min(index, items.length));
  const nextItems = [...items];
  nextItems.splice(safeIndex, 0, item);
  return nextItems;
};

const reindexColumns = (columns: TDraftValueChainBoardColumn[]): TDraftValueChainBoardColumn[] => (
  columns.map((column, index) => ({
    ...column,
    orderIndex: index,
  }))
);

const reindexUnits = (units: TDraftValueChainUnit[]): TDraftValueChainUnit[] => (
  units.map((unit, index) => ({
    ...unit,
    orderIndex: index,
  }))
);

const getDraftUnitInsertIndex = (
  draftUnit: Pick<IAIDraftUnit, 'insertAtIndex' | 'orderIndex'>,
  fallbackIndex: number,
): number => {
  if (isFiniteNumber(draftUnit.insertAtIndex)) {
    return draftUnit.insertAtIndex;
  }

  if (isFiniteNumber(draftUnit.orderIndex)) {
    return draftUnit.orderIndex;
  }

  return fallbackIndex;
};

const cloneUnit = (unit: IValueChainUnit): TDraftValueChainUnit => ({
  ...unit,
  children: unit.children.map(cloneUnit),
  records: unit.records?.map((record) => ({ ...record })) ?? [],
  lConToUnits: unit.lConToUnits ? [...unit.lConToUnits] : [],
  sConToUnits: unit.sConToUnits ? [...unit.sConToUnits] : [],
  gsConToUnits: unit.gsConToUnits ? [...unit.gsConToUnits] : [],
  mConToUnits: unit.mConToUnits ? [...unit.mConToUnits] : [],
});

const cloneBoard = (board: IValueChainBoard): IValueChainBoard => ({
  ...board,
  columns: board.columns.map((column) => ({
    ...column,
    units: column.units.map(cloneUnit),
  })),
});

const getDraftUnitKey = (
  draftUnit: Pick<
  IAIDraftUnit,
  'id' | 'sessionId' | 'insertAtBoardId' | 'insertAtIndex' | 'orderIndex' | 'name' | 'type'
  >,
): string => (
  isFiniteNumber(draftUnit.id)
    ? `${draftUnit.id}`
    : [
      draftUnit.sessionId,
      draftUnit.insertAtBoardId,
      draftUnit.insertAtIndex,
      draftUnit.orderIndex,
      draftUnit.type,
      draftUnit.name,
    ].join('__')
);

const getConnectionFieldName = (
  connectionType: string,
): 'lConToUnits' | 'sConToUnits' | 'gsConToUnits' | 'mConToUnits' | null => {
  switch (connectionType) {
    case VALUE_CHAIN_CONNECTION_TYPES.LOGICAL:
      return 'lConToUnits';
    case VALUE_CHAIN_CONNECTION_TYPES.SERVICE:
      return 'sConToUnits';
    case VALUE_CHAIN_CONNECTION_TYPES.SUPPLIER_OF_GOODS:
      return 'gsConToUnits';
    case VALUE_CHAIN_CONNECTION_TYPES.MANUFACTURE:
      return 'mConToUnits';
    default:
      return null;
  }
};

const getBusinessUnitType = (
  draftUnit: IAIDraftUnit,
  record: Partial<IValueChainRecord>,
): IValueChainRecord['businessUnitType'] => {
  if (
    draftUnit.subType === BUSINESS_UNIT_TYPES.LOGICAL
    || draftUnit.subType === BUSINESS_UNIT_TYPES.PHYSICAL
  ) {
    return draftUnit.subType;
  }

  return record.businessUnitType;
};

const createDraftRecord = (
  draftUnit: IAIDraftUnit,
  recordId: number,
): IValueChainRecord => {
  const draftRecord = draftUnit.record as Partial<IValueChainRecord>;

  return {
    id: toNumber(draftRecord.id, recordId),
    archived: Boolean(draftRecord.archived),
    name: toString(draftRecord.name, draftUnit.name),
    code: toString(draftRecord.code),
    uniqueIdentifier: toString(draftRecord.uniqueIdentifier),
    profitCenterIds: toStringArray(draftRecord.profitCenterIds),
    businessUnitType: getBusinessUnitType(draftUnit, draftRecord),
    startDate: toString(draftRecord.startDate),
    endDate: toString(draftRecord.endDate),
    naceSection: toString(draftRecord.naceSection) || undefined,
    addressCountry: toString(draftRecord.addressCountry) || undefined,
    upstreamTier: toString(draftRecord.upstreamTier) || undefined,
    totalRevenueRolling12MPercents: isFiniteNumber(draftRecord.totalRevenueRolling12MPercents)
      ? draftRecord.totalRevenueRolling12MPercents
      : undefined,
    totalRevenueRolling12MActual: draftRecord.totalRevenueRolling12MActual,
    totalGrossMarginRolling12MPercents: isFiniteNumber(draftRecord.totalGrossMarginRolling12MPercents)
      ? draftRecord.totalGrossMarginRolling12MPercents
      : undefined,
    totalGrossMarginRolling12MActual: draftRecord.totalGrossMarginRolling12MActual,
    wipFinishedGoodsStock: draftRecord.wipFinishedGoodsStock,
    keySupplierTier1Total: draftRecord.keySupplierTier1Total,
    keySupplierTier1OfTotal: toString(draftRecord.keySupplierTier1OfTotal) || undefined,
    relianceOnFreeNaturalResources: Array.isArray(draftRecord.relianceOnFreeNaturalResources)
      ? draftRecord.relianceOnFreeNaturalResources as string[]
      : undefined,
    criticalityRating: toString(draftRecord.criticalityRating) || undefined,
    netBookValue: draftRecord.netBookValue,
    assetCategory: Array.isArray(draftRecord.assetCategory)
      ? draftRecord.assetCategory as string[]
      : undefined,
    estimated: typeof draftRecord.estimated === 'boolean' ? draftRecord.estimated : undefined,
    totalCost: draftRecord.totalCost,
    virtualProperties: draftRecord.virtualProperties,
  };
};

const createDraftColumn = (draftColumn: IAIDraftColumn, index: number): TDraftValueChainBoardColumn => ({
  id: getDraftColumnId(index),
  name: draftColumn.name,
  orderIndex: draftColumn.insertAtIndex,
  type: draftColumn.type,
  units: [],
  draft: true,
});

const createDraftUnit = (draftUnit: IAIDraftUnit, index: number): TDraftValueChainUnit => ({
  id: getDraftUnitId(isFiniteNumber(draftUnit.id) ? draftUnit.id : -1, index),
  parentId: 0,
  name: draftUnit.name || '',
  orderIndex: getDraftUnitInsertIndex(draftUnit, index),
  type: draftUnit.type,
  records: [createDraftRecord(draftUnit, getDraftRecordId(isFiniteNumber(draftUnit.id) ? draftUnit.id : -1, index))],
  children: [],
  lConToUnits: [],
  sConToUnits: [],
  gsConToUnits: [],
  mConToUnits: [],
  virtualProperties: (draftUnit.record as Partial<IValueChainRecord>).virtualProperties,
  draft: true,
  draftServerId: isFiniteNumber(draftUnit.id) ? draftUnit.id : undefined,
});

const collectAllUnits = (units: TDraftValueChainUnit[], unitsMap: Map<number, TDraftValueChainUnit>): void => {
  units.forEach((unit) => {
    unitsMap.set(unit.id, unit);
    collectAllUnits(unit.children as TDraftValueChainUnit[], unitsMap);
  });
};

const buildDraftConnection = (
  connection: IAIDraftConnection,
  targetId: number,
  connectionId: number,
): TDraftConnection | null => {
  const connectionData = (
    connection.data && typeof connection.data === 'object'
      ? connection.data
      : {}
  ) as Record<string, unknown>;

  switch (connection.type) {
    case VALUE_CHAIN_CONNECTION_TYPES.LOGICAL:
      return {
        id: connectionId,
        toId: targetId,
        connectionType: VALUE_CHAIN_CONNECTION_TYPES.LOGICAL,
        comment: toString(connectionData.comment),
        ownership: isFiniteNumber(connectionData.ownership) ? connectionData.ownership : undefined,
        draft: true,
      };
    case VALUE_CHAIN_CONNECTION_TYPES.SERVICE:
      return {
        id: connectionId,
        toId: targetId,
        connectionType: VALUE_CHAIN_CONNECTION_TYPES.SERVICE,
        comment: toString(connectionData.comment),
        ownership: isFiniteNumber(connectionData.ownership) ? connectionData.ownership : undefined,
        serviceProviderType: toString(connectionData.serviceProviderType),
        monetaryCurrency: toString(connectionData.monetaryCurrency),
        monetaryValue: toNumber(connectionData.monetaryValue),
        draft: true,
      };
    case VALUE_CHAIN_CONNECTION_TYPES.SUPPLIER_OF_GOODS:
      return {
        id: connectionId,
        toId: targetId,
        transportMethods: toStringArray(
          connectionData.transportMethods,
        ) as IValueChainGoodsConnection['transportMethods'],
        distance: toString(connectionData.distance),
        monetaryValue: toNumber(connectionData.monetaryValue),
        monetaryCurrency: toString(connectionData.monetaryCurrency),
        quantity: toNumber(connectionData.quantity),
        quantityUnit: toString(connectionData.quantityUnit),
        draft: true,
      };
    default:
      return null;
  }
};

const resolveDraftRuntimeUnitIds = (
  draftUnits: Array<IAIDraftUnit | null> | undefined,
  draftUnitIdByKey: Map<string, number>,
): number[] => (
  (draftUnits || [])
    .filter((draftUnit): draftUnit is IAIDraftUnit => Boolean(draftUnit))
    .map((draftUnit) => draftUnitIdByKey.get(getDraftUnitKey(draftUnit)))
    .filter((unitId): unitId is number => typeof unitId === 'number')
);

const resolveRuntimeUnitId = (
  unitId: number,
  draftUnitIdByServerId: Map<number, number>,
): number => {
  const runtimeDraftUnitId = draftUnitIdByServerId.get(unitId);

  if (typeof runtimeDraftUnitId === 'number') {
    return runtimeDraftUnitId;
  }

  return unitId;
};

const resolveConnectionUnitIds = ({
  units,
  draftUnits,
  directUnitId,
  directDraftUnitId,
  draftUnitIdByKey,
  draftUnitIdByServerId,
}: {
  units?: IAIDraftConnectionUnit[],
  draftUnits?: Array<IAIDraftUnit | null>,
  directUnitId?: number | null,
  directDraftUnitId?: number | null,
  draftUnitIdByKey: Map<string, number>,
  draftUnitIdByServerId: Map<number, number>,
}): number[] => {
  const resolvedIds = new Set<number>();

  (units || []).forEach((unit) => {
    if (isFiniteNumber(unit.id)) {
      resolvedIds.add(resolveRuntimeUnitId(unit.id, draftUnitIdByServerId));
    }
  });

  resolveDraftRuntimeUnitIds(draftUnits, draftUnitIdByKey)
    .forEach((unitId) => resolvedIds.add(unitId));

  if (isFiniteNumber(directUnitId)) {
    resolvedIds.add(resolveRuntimeUnitId(directUnitId, draftUnitIdByServerId));
  }

  if (isFiniteNumber(directDraftUnitId)) {
    const runtimeDraftUnitId = draftUnitIdByServerId.get(directDraftUnitId);

    if (typeof runtimeDraftUnitId === 'number') {
      resolvedIds.add(runtimeDraftUnitId);
    }
  }

  return Array.from(resolvedIds);
};

const appendDraftConnections = (
  boards: IValueChainBoard[],
  draftUnitItem: IAIDraftUnitItem,
  draftUnitIdByKey: Map<string, number>,
  draftUnitIdByServerId: Map<number, number>,
): void => {
  const allUnitsById = new Map<number, TDraftValueChainUnit>();

  boards.forEach((board) => {
    board.columns.forEach((column) => {
      collectAllUnits(column.units as TDraftValueChainUnit[], allUnitsById);
    });
  });

  const processedConnections = new Set<string>();
  const draftConnections = [
    ...draftUnitItem.draftUnits.flatMap((unit) => unit?.draftConnectionToUnits || []),
    ...(draftUnitItem.draftConnections || []),
  ];

  draftConnections.forEach((connection, index) => {
    if (!connection) {
      return;
    }

    const connectionFieldName = getConnectionFieldName(connection.type);
    if (!connectionFieldName) {
      return;
    }

    const fromUnits = connection.fromUnit || [];
    const fromDraftUnits = connection.fromDraftUnit || [];
    const toUnits = connection.toUnit || [];
    const toDraftUnits = connection.toDraftUnit || [];

    const sourceIds = resolveConnectionUnitIds({
      units: fromUnits,
      draftUnits: fromDraftUnits,
      directUnitId: connection.fromId,
      directDraftUnitId: connection.fromDraftId,
      draftUnitIdByKey,
      draftUnitIdByServerId,
    });

    const targetIds = resolveConnectionUnitIds({
      units: toUnits,
      draftUnits: toDraftUnits,
      directUnitId: connection.toId,
      directDraftUnitId: connection.toDraftId,
      draftUnitIdByKey,
      draftUnitIdByServerId,
    });

    sourceIds.forEach((sourceId) => {
      const sourceUnit = allUnitsById.get(sourceId);

      if (!sourceUnit) {
        return;
      }

      targetIds.forEach((targetId) => {
        if (sourceId === targetId) {
          return;
        }

        if (!allUnitsById.has(targetId)) {
          return;
        }

        const dedupeKey = `${sourceId}_${targetId}_${connectionFieldName}`;
        if (processedConnections.has(dedupeKey)) {
          return;
        }

        const nextConnection = buildDraftConnection(
          connection,
          targetId,
          getDraftConnectionId(index + processedConnections.size),
        );

        if (!nextConnection) {
          return;
        }

        if (connectionFieldName === 'lConToUnits') {
          sourceUnit.lConToUnits = [
            ...(sourceUnit.lConToUnits || []),
            nextConnection as IValueChainLogicalConnection & { draft?: boolean },
          ];
        }

        if (connectionFieldName === 'sConToUnits') {
          sourceUnit.sConToUnits = [
            ...(sourceUnit.sConToUnits || []),
            nextConnection as IValueChainServiceConnection & { draft?: boolean },
          ];
        }

        if (connectionFieldName === 'gsConToUnits') {
          sourceUnit.gsConToUnits = [
            ...(sourceUnit.gsConToUnits || []),
            nextConnection as IValueChainGoodsConnection & { draft?: boolean },
          ];
        }

        if (connectionFieldName === 'mConToUnits') {
          sourceUnit.mConToUnits = [
            ...(sourceUnit.mConToUnits || []),
            nextConnection as IValueChainManufactureConnection & { draft?: boolean },
          ];
        }

        processedConnections.add(dedupeKey);
      });
    });
  });
};

const formatterAIDraftUnits = (
  boardResponse: IGetValueChainBoardResponse,
  draftUnitItem: IAIDraftUnitItem,
): IGetValueChainBoardResponse => {
  const boards = boardResponse.data.boards.map(cloneBoard);
  const draftUnitIdByKey = new Map<string, number>();
  const draftUnitIdByServerId = new Map<number, number>();
  const runtimeDraftColumnIdByServerId = new Map<number, number>();

  draftUnitItem.draftColumns.forEach((draftColumn, index) => {
    const board = boards.find((boardItem) => boardItem.id === draftColumn.insertAtBoardId);

    if (!board) {
      return;
    }

    const nextDraftColumn = createDraftColumn(draftColumn, index);

    board.columns = reindexColumns(
      insertAtIndex(
        board.columns as TDraftValueChainBoardColumn[],
        nextDraftColumn,
        draftColumn.insertAtIndex,
      ),
    );
    runtimeDraftColumnIdByServerId.set(draftColumn.id, nextDraftColumn.id);
  });

  const columnsById = new Map<number, TDraftValueChainBoardColumn>();
  boards.forEach((board) => {
    board.columns.forEach((column) => {
      columnsById.set(column.id, column as TDraftValueChainBoardColumn);
    });
  });

  draftUnitItem.draftUnits.forEach((draftUnit, index) => {
    const runtimeDraftColumnId = isFiniteNumber(draftUnit.draftColumnId)
      ? runtimeDraftColumnIdByServerId.get(draftUnit.draftColumnId)
      : undefined;
    const targetBoard = isFiniteNumber(draftUnit.insertAtBoardId)
      ? boards.find((board) => board.id === draftUnit.insertAtBoardId)
      : undefined;
    const targetColumnByIndex = targetBoard?.columns.find((column) => (
      column.orderIndex === getDraftUnitInsertIndex(draftUnit, index)
    ));
    const targetColumn = (typeof runtimeDraftColumnId === 'number'
      ? columnsById.get(runtimeDraftColumnId)
      : undefined)
      || (isFiniteNumber(draftUnit.columnId)
        ? columnsById.get(draftUnit.columnId)
        : undefined)
      || (isFiniteNumber(draftUnit.insertAtBoardId)
        ? columnsById.get(draftUnit.insertAtBoardId)
        : undefined)
      || (targetColumnByIndex as TDraftValueChainBoardColumn | undefined)
      || (targetBoard && targetBoard.columns.length > 0
        ? targetBoard.columns[
          Math.min(getDraftUnitInsertIndex(draftUnit, index), targetBoard.columns.length - 1)
        ] as TDraftValueChainBoardColumn
        : undefined);

    if (!targetColumn) {
      return;
    }

    const nextDraftUnit = createDraftUnit(draftUnit, index);
    draftUnitIdByKey.set(getDraftUnitKey(draftUnit), nextDraftUnit.id);
    if (isFiniteNumber(draftUnit.id)) {
      draftUnitIdByServerId.set(draftUnit.id, nextDraftUnit.id);
    }
    targetColumn.units = reindexUnits(
      insertAtIndex(
        targetColumn.units as TDraftValueChainUnit[],
        nextDraftUnit,
        getDraftUnitInsertIndex(draftUnit, targetColumn.units.length),
      ),
    );
  });

  appendDraftConnections(boards, draftUnitItem, draftUnitIdByKey, draftUnitIdByServerId);

  return {
    ...boardResponse,
    data: {
      ...boardResponse.data,
      boards,
    },
  };
};

export default formatterAIDraftUnits;
